/**
 * QuickPeekPage — Preliminary projection based on intake defaults
 *
 * After the intake interview, shows the user a single-screen preview of their
 * retirement projections using age-based statistical defaults. This "payoff"
 * screen motivates them to refine their data by showing what the data is for.
 *
 * Content:
 * - Personalized header
 * - Preliminary Readiness Score
 * - Projected retirement amount
 * - 2–3 archetype-based insights
 * - "Your Starting Assumptions" panel (transparent defaults)
 * - Two CTAs: "Make this more accurate" + "Take me to my dashboard"
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import { useAuth } from "../../hooks/useAuth";
import { getIntakeProfile } from "../../services/intakeService";
import { completeOnboarding } from "../../services/onboardingService";
import { getAssumptionDefaults } from "../../types/intake";
import type { IntakeProfile, AssumptionDefaults } from "../../types/intake";
import { calculateRetirement } from "../../store/retirementStore";
import type {
  RetirementResult,
  RetirementData,
} from "../../store/retirementStore";
import { createLogger } from "../../utils/logger";

const quickPeekLogger = createLogger({ prefix: "QuickPeek" });

/** Build RetirementData from intake defaults for a preliminary projection. */
function buildRetirementDataFromDefaults(
  profile: IntakeProfile,
  defaults: AssumptionDefaults
): RetirementData {
  const currentAge = estimateAgeFromLifeStage(profile.lifeStage);
  return {
    currentAge,
    retirementAge: defaults.retirementAge,
    lifeExpectancy: defaults.lifeExpectancy,
    currentSavings: 0,
    monthlyContribution: 0,
    annualReturn: defaults.expectedReturnRate,
    inflationRate: defaults.inflationRate,
    monthlyExpenses: 4000,
    expensesAtRetirement: defaults.expensesAtRetirement,
    currency: "CAD",
    debts: {
      mortgage: { balance: 0, interestRate: 0, monthlyPayment: 0 },
      creditCard: { balance: 0, interestRate: 0, monthlyPayment: 0 },
      carLoan: { balance: 0, interestRate: 0, monthlyPayment: 0 },
      lineOfCredit: { balance: 0, interestRate: 0, monthlyPayment: 0 },
      studentLoan: { balance: 0, interestRate: 0, monthlyPayment: 0 },
      otherLiabilities: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    },
  };
}

/** Estimate a representative age from the life stage answer. */
function estimateAgeFromLifeStage(
  lifeStage: IntakeProfile["lifeStage"]
): number {
  switch (lifeStage) {
    case "starting-out":
      return 25;
    case "building":
      return 35;
    case "peak-earning":
      return 48;
    case "approaching":
      return 58;
    case "retired":
      return 68;
  }
}

/** Get archetype-based insights for the user's profile. */
function getInsights(profile: IntakeProfile): string[] {
  const insights: string[] = [];

  switch (profile.lifeStage) {
    case "starting-out":
      insights.push(
        "Starting to save in your 20s is your biggest advantage — time is on your side."
      );
      break;
    case "building":
      insights.push(
        "Your career growth years are ideal for building a strong savings habit."
      );
      break;
    case "peak-earning":
      insights.push(
        "Peak earning years give you the most room to accelerate retirement savings."
      );
      break;
    case "approaching":
      insights.push(
        "You're close — every dollar saved now has maximum impact on your retirement comfort."
      );
      break;
    case "retired":
      insights.push(
        "Managing your withdrawals carefully can make your savings last significantly longer."
      );
      break;
  }

  if (
    profile.employment === "employed-pension" ||
    profile.employment === "multiple"
  ) {
    insights.push(
      "Your employer pension is a powerful foundation for retirement income."
    );
  }

  if (profile.housing === "own-free") {
    insights.push(
      "Owning your home mortgage-free reduces your retirement expenses significantly."
    );
  } else if (profile.housing === "own-investment") {
    insights.push(
      "Investment property can provide additional retirement income streams."
    );
  }

  if (profile.debtSituation.includes("debt-free")) {
    insights.push("Being debt-free gives you a strong head start.");
  }

  return insights.slice(0, 3);
}

/** Format a number as currency. */
function formatAmount(amount: number): string {
  return `$${Math.round(amount).toLocaleString()}`;
}

/** Format a decimal rate as percentage. */
function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`.replace(/\.0%$/, "%");
}

export default function QuickPeekPage() {
  const navigate = useNavigate();
  const { user, userProfile, updateOnboardingCompleted } = useAuth();
  const [profile, setProfile] = useState<IntakeProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.userId) return;

    let cancelled = false;

    async function loadProfile() {
      const result = await getIntakeProfile(user!.userId);
      if (cancelled) return;

      if (result.ok && result.value) {
        setProfile(result.value);
      } else {
        quickPeekLogger.warn("No intake profile found, using defaults");
      }
      setLoading(false);
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const defaults = useMemo(
    () => getAssumptionDefaults(profile?.lifeStage ?? "building"),
    [profile?.lifeStage]
  );

  const result: RetirementResult | null = useMemo(() => {
    if (!profile) return null;
    const data = buildRetirementDataFromDefaults(profile, defaults);
    return calculateRetirement(data);
  }, [profile, defaults]);

  const insights = useMemo(
    () => (profile ? getInsights(profile) : []),
    [profile]
  );

  const handlePrimaryCTA = useCallback(() => {
    navigate("/onboarding/data-entry", { replace: true });
  }, [navigate]);

  const handleSecondaryCTA = useCallback(async () => {
    if (user) {
      await completeOnboarding(user.userId);
      updateOnboardingCompleted(true);
    }
    navigate("/dashboard", { replace: true });
  }, [user, navigate, updateOnboardingCompleted]);

  if (loading) {
    return (
      <div className="quick-peek-page" id="quick-peek">
        <div className="quick-peek-loading">Loading your results...</div>
      </div>
    );
  }

  return (
    <div className="quick-peek-page" id="quick-peek">
      {/* Personalized header */}
      <h1 className="quick-peek-header" id="quick-peek-header">
        Here&apos;s your starting point, {userProfile?.firstName ?? "there"}
      </h1>

      {/* Readiness Score */}
      {result && (
        <div className="quick-peek-score" id="quick-peek-score">
          <span className="quick-peek-score-value">
            {result.readinessScore}
          </span>
          <span className="quick-peek-score-label">Retirement Readiness</span>
        </div>
      )}

      {/* Projected Amount */}
      {result && (
        <div className="quick-peek-projection" id="quick-peek-projection">
          <p>
            At age {defaults.retirementAge}, you could have approximately{" "}
            <strong>{formatAmount(result.finalAmount)}</strong>
          </p>
        </div>
      )}

      {/* Archetype Insights */}
      {insights.length > 0 && (
        <div className="quick-peek-insights" id="quick-peek-insights">
          <h2>What this means for you</h2>
          <ul>
            {insights.map((insight, i) => (
              <li key={i} className="quick-peek-insight-item">
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Assumptions Panel */}
      <div className="quick-peek-assumptions" id="quick-peek-assumptions">
        <h2>Your Starting Assumptions</h2>
        <p className="quick-peek-assumptions-intro">
          We&apos;re using these reasonable defaults based on Canadian averages
          for your age group:
        </p>
        <ul className="quick-peek-assumptions-list">
          <li>Retirement age: {defaults.retirementAge}</li>
          <li>Planning to age: {defaults.lifeExpectancy}</li>
          <li>
            Investment growth: {formatRate(defaults.expectedReturnRate)} / year
          </li>
          <li>Inflation: {formatRate(defaults.inflationRate)} / year</li>
          <li>
            Spending in retirement:{" "}
            {Math.round((1 + defaults.expensesAtRetirement) * 100)}% of current
          </li>
        </ul>
        <p className="quick-peek-assumptions-customizable">
          You can customize these anytime in Settings.
        </p>
      </div>

      {/* CTAs */}
      <div className={cn("quick-peek-ctas")} id="quick-peek-ctas">
        <button
          type="button"
          className="quick-peek-cta-primary"
          onClick={handlePrimaryCTA}
          aria-label="Let's make this more accurate"
        >
          Let&apos;s make this more accurate →
        </button>
        <button
          type="button"
          className="quick-peek-cta-secondary"
          onClick={handleSecondaryCTA}
          aria-label="Take me to my dashboard"
        >
          Take me to my dashboard →
        </button>
      </div>
    </div>
  );
}
